/* ═══════════════════════════════════════════════════════════════
 * DRAW TOOLS — tekengereedschap: trendlijn (schuin), horizontale
 * lijn (recht), ray, vrije hand (brush), rechthoek, fibonacci,
 * fibonacci-transient (enkel het 0.5-niveau, loopt door tot de
 * rechterrand met de prijs erbij — voor PCG-transients), meetlat
 * (prijsverschil/tijdsduur/volume tussen twee punten, zoals
 * TradingView's "Date and price range"-tool) en tekst.
 * Ankerpunten worden opgeslagen in DATA-ruimte (tijd + prijs)
 * zodat lijnen vastgepind blijven bij pan/zoom, timeframe-wissel en
 * live candle-updates.
 *
 * 1:1 poort van TERMINAL.drawTools uit terminal_wyckoffpcg.html.
 * Tekenen loopt via een officiële Lightweight-Charts PRIMITIVE
 * (ISeriesPrimitive): de library beheert de render-loop, DPR, z-order
 * (boven de serie) en het hertekenen bij pan/zoom/data-updates. Wij
 * handelen alleen de muis-interactie zelf af. Muis-events worden op
 * de container beluisterd (canvas blijft pointerEvents:none) zodat
 * de chart-interactie intact blijft; tijdens het tekenen worden
 * scroll/scale tijdelijk uitgezet.
 *
 * Heeft zijn eigen live-sync-API (onLocalEdit/applyRemote/getAllLines)
 * voor coach↔student-broadcast — dit is bewust nog NIET aangesloten
 * op de al gebouwde cafe-platform Live Session (PartyServer); dat is
 * een latere, losse stap (twee onafhankelijke broadcast-systemen
 * reconciliëren).
 * ═══════════════════════════════════════════════════════════════ */

import { CONFIG as CFG } from "./config";

const C = CFG.colors;

const LINE_COL = C.accent; // #f0b90b — dubforvet-geel
const LINE_W = 1;
const HIT_PX = 7; // klik-tolerantie (px)
const HANDLE_R = 3.5; // radius eindpunt-handles
const GRAB_PX = 9; // grijp-tolerantie voor eindpunten
const BRUSH_MIN_PX = 2.5; // min. pixelafstand tussen brush-punten (thinning)
const BRUSH_MAX_PTS = 1500; // veiligheidscap op punten per streek
// Fibonacci-niveaus + kleur per niveau (zelfde set als Martijns TradingView-
// template: 1/8- en 1/16-verdeling + 2.618/3.618 extensies). Lijnen lopen
// door tot de rechterrand van de chart ("Extend lines right").
const FIB_C = { purple: "#ab47bc", gray: "#787b86", red: "#f23645", orange: "#ff9800", blue: "#2962ff" };
const FIB_LEVELS = [
  { r: 0, c: FIB_C.purple },
  { r: 0.03125, c: FIB_C.gray },
  { r: 0.0625, c: FIB_C.blue },
  { r: 0.125, c: FIB_C.gray },
  { r: 0.1875, c: FIB_C.blue },
  { r: 0.25, c: FIB_C.gray },
  { r: 0.3125, c: FIB_C.blue },
  { r: 0.375, c: FIB_C.gray },
  { r: 0.4375, c: FIB_C.blue },
  { r: 0.5, c: FIB_C.orange },
  { r: 0.5625, c: FIB_C.red },
  { r: 0.625, c: FIB_C.gray },
  { r: 0.6875, c: FIB_C.red },
  { r: 0.75, c: FIB_C.gray },
  { r: 0.8125, c: FIB_C.red },
  { r: 0.875, c: FIB_C.gray },
  { r: 0.9375, c: FIB_C.red },
  { r: 0.96875, c: FIB_C.gray },
  { r: 1, c: FIB_C.purple },
  { r: 2.618, c: FIB_C.red },
  { r: 3.618, c: FIB_C.gray },
];
const FIB_RATIOS = FIB_LEVELS.map((l) => l.r);
const FIB_LABEL_OFFSET_BARS = 50; // labels staan 50 bars rechts van de laatste candle
const FIB_TR_RATIO = 0.5; // fibonacci-transient: alleen het halverwege-niveau

// Opties in het lijn-editor paneeltje
export const PALETTE = ["#f0b90b", "#26de81", "#e0455b", "#3aa0c8", "#9b8cff", "#e6e8ea"];
export const WIDTHS = [1, 2, 3]; // dun / normaal / dik
export const STYLES = ["solid", "dashed", "dotted"];
export const TEXT_SIZES = [8, 10, 12, 14]; // tekstgroottes in px (voor de tekst-tool + fib-transient label)

// Streeppatroon voor een gegeven stijl, mee-schalend met de dikte
function dashFor(style, w) {
  if (style === "dashed") return [Math.max(4, w * 4), Math.max(3, w * 3)];
  if (style === "dotted") return [Math.max(1, w), Math.max(2, w * 2)];
  return [];
}

export function createDrawTools(container, getApi, getCandles) {
  // Tekenen loopt via een officiële Lightweight-Charts PRIMITIVE
  // (ISeriesPrimitive, zie plugins/series-primitives). De library beheert de
  // render-loop, DPR, z-order (boven de serie) en het hertekenen bij
  // pan/zoom/data-updates. Wij handelen alleen de muis-interactie zelf af.
  container.style.position = "relative"; // voor de absolute editor-popover
  let ctx = null; // context, per frame door de renderer gezet
  let paneW = 0, paneH = 0; // media-afmetingen van de pane
  let requestUpdate = null; // van attached(): vraag om hertekenen

  // ── State ────────────────────────────────────────────────────
  const lines = []; // { id, type, a:{x,y}, b:{x,y}, color, width, style }
  let idSeq = 0;
  function newId() { return "ln_" + Date.now().toString(36) + "_" + (++idSeq) + Math.floor(Math.random() * 1e4).toString(36); }
  function cloneLine(l) {
    const c = { id: l.id, type: l.type, color: l.color, width: l.width, style: l.style };
    if (l.points) c.points = l.points.map((p) => ({ x: p.x, y: p.y })); // brush
    else { c.a = { ...l.a }; if (l.b) c.b = { ...l.b }; }
    if (l.text !== undefined) c.text = l.text; // tekst
    if (l.size !== undefined) c.size = l.size; // tekstgrootte
    return c;
  }
  let localEditCb = null; // callback bij elke lokale wijziging (voor broadcast)
  let applyingRemote = false; // onderdruk emit terwijl we een remote event toepassen
  let lastUpdateEmit = 0; // throttle voor live 'update' tijdens slepen
  function emit(type, payload) { if (applyingRemote || !localEditCb) return; localEditCb(Object.assign({ type }, payload)); }
  let draft = null; // lijn-in-opbouw
  let tool = "cursor"; // 'cursor'|'trend'|'ray'|'hline'|'brush'|'rect'|'fib'|'fibtr'|'text'
  let magnet = false; // snap naar OHLC van candle onder cursor
  let selected = -1; // index geselecteerde lijn (cursor-modus)
  let hover = -1; // index gehoverde lijn (cursor-modus)
  let curPx = 0, curPy = 0; // laatste cursor-positie in canvas-px
  let toolChangeCb = null;
  let snapPreview = null; // {x,y} data-anker voor magneet-preview
  let drag = null; // { part:'p1'|'p2'|'line'|'hline', idx, startRaw, origA, origB, moved }
  let suppressClick = false; // onderdruk de klik die na een sleep-actie volgt
  let brushLastPx = { x: 0, y: 0 }; // laatst toegevoegde brush-punt (px), voor thinning
  const curStyle = { color: LINE_COL, width: LINE_W, style: "solid", size: 12 }; // laatst gekozen stijl
  let clipboard = null; // gekopieerde lijn (Ctrl+C), voor Ctrl+V / duplicaat-knop
  // Toon/verberg-toggle: puur lokaal (niet gebroadcast, niet opgeslagen) —
  // een manier voor de coach om het chart tijdelijk leeg te zien zonder de
  // tekeningen zelf kwijt te raken. Tekeningen blijven gewoon in `lines`
  // staan en worden niet verwijderd; alleen renderAll()/hit-testing slaan
  // ze over zolang dit uit staat.
  let linesVisible = true;

  // ── Data-ruimte ⇄ pixels ─────────────────────────────────────
  function toPx(pt) {
    const { chart, candleSeries } = getApi();
    const L = timeToLogical(pt.x); // pt.x = TIJD
    const x = L == null ? null : chart.timeScale().logicalToCoordinate(L);
    const y = candleSeries.priceToCoordinate(pt.y);
    if (x == null || y == null) return null;
    return { x, y };
  }
  function priceAt(py) {
    const { candleSeries } = getApi();
    return candleSeries.coordinateToPrice(py);
  }
  function logicalAt(px) {
    return getApi().chart.timeScale().coordinateToLogical(px);
  }

  // ── Tijd ⇄ (fractionele) logische index ──────────────────────
  // Ankers slaan TIJD op (timeframe-onafhankelijk). De library kan tijd niet
  // continu naar een coördinaat mappen, dus interpoleren/extrapoleren we zelf
  // met de huidige candle-array. Zo blijven tekeningen op hun plek bij het
  // wisselen van timeframe.
  function logicalToTimeInterp(L) {
    const cs = getCandles();
    if (L == null || !cs || !cs.length) return null;
    const n = cs.length;
    if (n === 1) return cs[0].time;
    if (L <= 0) { const step = cs[1].time - cs[0].time; return cs[0].time + L * step; }
    if (L >= n - 1) { const step = cs[n - 1].time - cs[n - 2].time; return cs[n - 1].time + (L - (n - 1)) * step; }
    const i = Math.floor(L), frac = L - i;
    return cs[i].time + frac * (cs[i + 1].time - cs[i].time);
  }
  function timeToLogical(t) {
    const cs = getCandles();
    if (t == null || !cs || !cs.length) return null;
    const n = cs.length;
    if (n === 1) return 0;
    if (t <= cs[0].time) { const step = (cs[1].time - cs[0].time) || 1; return (t - cs[0].time) / step; }
    if (t >= cs[n - 1].time) { const step = (cs[n - 1].time - cs[n - 2].time) || 1; return (n - 1) + (t - cs[n - 1].time) / step; }
    let lo = 0, hi = n - 1;
    while (lo < hi) { const m = (lo + hi + 1) >> 1; if (cs[m].time <= t) lo = m; else hi = m - 1; }
    const span = (cs[lo + 1].time - cs[lo].time) || 1;
    return lo + (t - cs[lo].time) / span;
  }

  // Cursor → data-anker (met optionele magneet-snap naar OHLC)
  // Ruwe cursor → data-anker (geen snap)
  function rawAnchor(px, py) { return { x: logicalToTimeInterp(logicalAt(px)), y: priceAt(py) }; }

  // Verschuift één data-anker met een vast aantal schermpixels (voor plak/
  // dupliceren: de kopie landt zichtbaar los van het origineel in plaats
  // van er precies bovenop). Valt terug op het ongewijzigde punt als de
  // pixel-conversie (tijdelijk) faalt, bv. net buiten het zichtbare bereik.
  function shiftPoint(pt, dxPx, dyPx) {
    const p = toPx(pt);
    if (!p) return { ...pt };
    return rawAnchor(p.x + dxPx, p.y + dyPx);
  }

  // Magneet: snap x naar candle-index (logische as), y naar dichtstbijzijnde O/H/L/C.
  function magnetAnchor(px, py) {
    const cs = getCandles();
    const L = logicalAt(px);
    let price = priceAt(py);
    if (L == null || price == null || !cs || !cs.length) return { x: L, y: price };
    const idx = Math.max(0, Math.min(cs.length - 1, Math.round(L)));
    const c = cs[idx];
    const oh = [c.open, c.high, c.low, c.close];
    let best = oh[0];
    for (const v of oh) if (Math.abs(v - price) < Math.abs(best - price)) best = v;
    return { x: c.time, y: best }; // TIJD (timeframe-onafhankelijk)
  }

  // Shift: dwing het bewegende punt in een rechte hoek t.o.v. het vaste punt
  // (horizontaal / verticaal / 45°), zoals de shift-modus op TradingView.
  function constrainToData(fixedPx, movePx) {
    const dx = movePx.x - fixedPx.x, dy = movePx.y - fixedPx.y;
    const step = Math.PI / 4;
    const ang = Math.round(Math.atan2(dy, dx) / step) * step;
    const len = Math.hypot(dx, dy);
    return {
      x: logicalToTimeInterp(logicalAt(fixedPx.x + Math.cos(ang) * len)),
      y: priceAt(fixedPx.y + Math.sin(ang) * len),
    };
  }

  // Uniforme anker-resolver: shift > magneet > ruw.
  function anchorFromPixel(px, py, ev, fixedPx) {
    if (ev && ev.shiftKey && fixedPx) return constrainToData(fixedPx, { x: px, y: py });
    if (magnet) return magnetAnchor(px, py);
    return rawAnchor(px, py);
  }

  // ── Hit-testing (pixels) ─────────────────────────────────────
  function distToSeg(px, py, ax, ay, bx, by) {
    const dx = bx - ax, dy = by - ay;
    const len2 = dx * dx + dy * dy;
    let t = len2 ? ((px - ax) * dx + (py - ay) * dy) / len2 : 0;
    t = Math.max(0, Math.min(1, t));
    const qx = ax + t * dx, qy = ay + t * dy;
    return Math.hypot(px - qx, py - qy);
  }
  function brushPx(line) {
    const out = [];
    for (const p of (line.points || [])) { const q = toPx(p); if (q) out.push(q); }
    return out;
  }
  function segPixels(line) {
    const w = paneW || container.clientWidth;
    if (line.type === "brush") {
      const pts = brushPx(line);
      if (!pts.length) return null;
      const f = pts[0], l = pts[pts.length - 1];
      return { ax: f.x, ay: f.y, bx: l.x, by: l.y };
    }
    if (line.type === "text") {
      const p = toPx(line.a);
      if (!p) return null;
      return { ax: p.x, ay: p.y, bx: p.x, by: p.y };
    }
    if (line.type === "hline") {
      const p = toPx(line.a);
      if (!p) return null;
      return { ax: 0, ay: p.y, bx: w, by: p.y };
    }
    const a = toPx(line.a), b = toPx(line.b);
    if (!a || !b) return null;
    if (line.type === "ray") {
      let ex = b.x, ey = b.y;
      if (b.x !== a.x) {
        const slope = (b.y - a.y) / (b.x - a.x);
        ex = w; ey = a.y + slope * (w - a.x);
      }
      return { ax: a.x, ay: a.y, bx: ex, by: ey };
    }
    return { ax: a.x, ay: a.y, bx: b.x, by: b.y };
  }
  function hitTest(px, py) {
    if (!linesVisible) return -1;
    for (let i = lines.length - 1; i >= 0; i--) {
      const ln = lines[i];
      if (ln.type === "brush") {
        const pts = brushPx(ln);
        for (let k = 1; k < pts.length; k++)
          if (distToSeg(px, py, pts[k - 1].x, pts[k - 1].y, pts[k].x, pts[k].y) <= HIT_PX) return i;
        continue;
      }
      const s = segPixels(ln);
      if (!s) continue;
      if (distToSeg(px, py, s.ax, s.ay, s.bx, s.by) <= HIT_PX) return i;
    }
    return -1;
  }
  function handleHit(px, py) {
    if (!linesVisible) return null;
    for (let i = lines.length - 1; i >= 0; i--) {
      const ln = lines[i];
      if (ln.type === "brush") {
        const pts = brushPx(ln);
        for (let k = 1; k < pts.length; k++)
          if (distToSeg(px, py, pts[k - 1].x, pts[k - 1].y, pts[k].x, pts[k].y) <= HIT_PX)
            return { idx: i, part: "line" };
        continue;
      }
      if (ln.type === "text") {
        if (textHit(ln, px, py)) return { idx: i, part: "line" };
        continue;
      }
      if (ln.type === "rect" || ln.type === "fib" || ln.type === "fibtr" || ln.type === "measure") {
        const a = toPx(ln.a), b = toPx(ln.b);
        if (a && b) {
          if (Math.hypot(px - a.x, py - a.y) <= GRAB_PX) return { idx: i, part: "p1" };
          if (Math.hypot(px - b.x, py - b.y) <= GRAB_PX) return { idx: i, part: "p2" };
          const body = ln.type === "rect" ? rectEdgesHit(a, b, px, py)
            : ln.type === "fib" ? fibLevelsHit(ln, a, b, px, py)
            : ln.type === "fibtr" ? fibTransientHit(ln, a, b, px, py)
            : measureBodyHit(a, b, px, py);
          if (body) return { idx: i, part: "line" };
        }
        continue;
      }
      if (ln.type !== "hline") {
        const pa = toPx(ln.a), pb = toPx(ln.b);
        if (pa && Math.hypot(px - pa.x, py - pa.y) <= GRAB_PX) return { idx: i, part: "p1" };
        if (pb && Math.hypot(px - pb.x, py - pb.y) <= GRAB_PX) return { idx: i, part: "p2" };
      }
      const s = segPixels(ln);
      if (!s) continue;
      if (ln.type === "hline") {
        if (Math.abs(py - s.ay) <= HIT_PX) return { idx: i, part: "hline" };
      } else if (distToSeg(px, py, s.ax, s.ay, s.bx, s.by) <= HIT_PX) {
        return { idx: i, part: "line" };
      }
    }
    return null;
  }

  function textHit(ln, px, py) {
    const p = toPx(ln.a);
    if (!p || !ln.text) return false;
    const size = ln.size || textSize(ln.width) || 16;
    const wpx = ln.text.length * size * 0.62;
    return px >= p.x - 3 && px <= p.x + wpx + 3 && py >= p.y - 3 && py <= p.y + size + 3;
  }
  function rectEdgesHit(a, b, px, py) {
    const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y);
    return distToSeg(px, py, x1, y1, x2, y1) <= HIT_PX || distToSeg(px, py, x2, y1, x2, y2) <= HIT_PX ||
      distToSeg(px, py, x2, y2, x1, y2) <= HIT_PX || distToSeg(px, py, x1, y2, x1, y1) <= HIT_PX;
  }
  // Meetlat is een gevulde box — overal binnenin mag 'm selecteren/slepen,
  // niet alleen op de randen (prettiger dan rectEdgesHit voor een vlak dat
  // je als geheel wil verplaatsen).
  function measureBodyHit(a, b, px, py) {
    const x1 = Math.min(a.x, b.x), x2 = Math.max(a.x, b.x), y1 = Math.min(a.y, b.y), y2 = Math.max(a.y, b.y);
    return px >= x1 && px <= x2 && py >= y1 && py <= y2;
  }
  function fibLevelsHit(ln, a, b, px, py) {
    const cs = getApi().candleSeries, pa = ln.a.y, pb = ln.b.y;
    const x1 = Math.min(a.x, b.x), x2 = paneW || container.clientWidth; // lijnen lopen door naar rechts
    for (const r of FIB_RATIOS) {
      const y = cs.priceToCoordinate(pa + (pb - pa) * r);
      if (y != null && distToSeg(px, py, x1, y, x2, y) <= HIT_PX) return true;
    }
    return false;
  }
  function fibTransientHit(ln, a, b, px, py) {
    const cs = getApi().candleSeries, pa = ln.a.y, pb = ln.b.y;
    const x1 = Math.min(a.x, b.x), x2 = paneW || container.clientWidth;
    const y = cs.priceToCoordinate(pa + (pb - pa) * FIB_TR_RATIO);
    return y != null && distToSeg(px, py, x1, y, x2, y) <= HIT_PX;
  }

  // ── Tekenen ──────────────────────────────────────────────────
  function strokeSeg(s, col, width, dash) {
    ctx.strokeStyle = col; ctx.lineWidth = width;
    ctx.setLineDash(dash || []);
    ctx.beginPath(); ctx.moveTo(s.ax, s.ay); ctx.lineTo(s.bx, s.by); ctx.stroke();
    ctx.setLineDash([]);
  }
  function strokeBrush(pts, col, width, dash) {
    if (pts.length < 2) return;
    ctx.strokeStyle = col; ctx.lineWidth = width;
    ctx.lineJoin = "round"; ctx.lineCap = "round";
    ctx.setLineDash(dash || []);
    ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y);
    for (let k = 1; k < pts.length; k++) ctx.lineTo(pts[k].x, pts[k].y);
    ctx.stroke(); ctx.setLineDash([]);
  }
  function drawHandles(p) {
    ctx.fillStyle = "#0b0e11"; ctx.strokeStyle = LINE_COL; ctx.lineWidth = 1.5;
    for (const pt of p) {
      ctx.beginPath(); ctx.arc(pt.x, pt.y, HANDLE_R, 0, Math.PI * 2);
      ctx.fill(); ctx.stroke();
    }
  }

  function paintShape(ln, isSel, isHov) {
    const col = ln.color, w = ln.width + (isSel ? 0.75 : 0), dash = dashFor(ln.style, ln.width);
    const hot = isSel || isHov;

    if (ln.type === "brush") { strokeBrush(brushPx(ln), col, w, dash); return; }
    if (ln.type === "text") { paintText(ln, hot); return; }
    if (ln.type === "fib") { paintFib(ln, w, dash, hot); return; }
    if (ln.type === "fibtr") { paintFibTransient(ln, w, dash, hot); return; }
    if (ln.type === "measure") { paintMeasure(ln, hot); return; }
    if (ln.type === "rect") {
      const a = toPx(ln.a), b = toPx(ln.b);
      if (!a || !b) return;
      const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y), rw = Math.abs(b.x - a.x), rh = Math.abs(b.y - a.y);
      ctx.save();
      ctx.globalAlpha = 0.08; ctx.fillStyle = col; ctx.fillRect(x, y, rw, rh); ctx.globalAlpha = 1;
      ctx.strokeStyle = col; ctx.lineWidth = w; ctx.setLineDash(dash);
      ctx.strokeRect(x, y, rw, rh); ctx.setLineDash([]);
      ctx.restore();
      if (hot) drawHandles([{ x: a.x, y: a.y }, { x: b.x, y: b.y }]);
      return;
    }

    const s = segPixels(ln);
    if (!s) return;
    strokeSeg(s, col, w, dash);
    if (hot) {
      if (ln.type === "hline") drawHandles([{ x: curPx, y: s.ay }]);
      else drawHandles([{ x: s.ax, y: s.ay }, { x: s.bx, y: s.by }]);
    }
  }

  function paintFib(ln, w, dash, hot) {
    const cs = getApi().candleSeries;
    const a = toPx(ln.a), b = toPx(ln.b);
    if (!a || !b) return;
    const pa = ln.a.y, pb = ln.b.y;
    const x1 = Math.min(a.x, b.x);
    const xRight = paneW || container.clientWidth; // "Extend lines right"
    const ys = FIB_LEVELS.map((l) => cs.priceToCoordinate(pa + (pb - pa) * l.r));
    ctx.save();
    // Zachte achtergrond tussen opeenvolgende niveaus, in de kleur van het niveau.
    ctx.globalAlpha = 0.05;
    for (let k = 0; k < ys.length - 1; k++) {
      if (ys[k] == null || ys[k + 1] == null) continue;
      ctx.fillStyle = FIB_LEVELS[k + 1].c;
      ctx.fillRect(x1, Math.min(ys[k], ys[k + 1]), xRight - x1, Math.abs(ys[k + 1] - ys[k]));
    }
    ctx.globalAlpha = 1;
    ctx.lineWidth = w;
    ctx.font = "10px 'JetBrains Mono', ui-monospace, monospace"; ctx.textBaseline = "middle"; ctx.textAlign = "left";
    // Label-positie: 50 bars rechts van de laatste candle (in de lege ruimte
    // rechts van de koers), maar nooit buiten de chart of links van de fib.
    const candles = getCandles();
    let xLabel = null;
    if (candles && candles.length) {
      xLabel = getApi().chart.timeScale().logicalToCoordinate(candles.length - 1 + FIB_LABEL_OFFSET_BARS);
    }
    const labelW = ctx.measureText("0.96875  000000.00").width + 6;
    if (xLabel == null || xLabel > xRight - labelW) xLabel = xRight - labelW;
    if (xLabel < x1 + 4) xLabel = x1 + 4;
    for (let k = 0; k < ys.length; k++) {
      const y = ys[k]; if (y == null) continue;
      const lvl = FIB_LEVELS[k];
      ctx.strokeStyle = lvl.c; ctx.fillStyle = lvl.c;
      ctx.setLineDash(dash);
      ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(xRight, y); ctx.stroke();
      ctx.setLineDash([]);
      const price = pa + (pb - pa) * lvl.r;
      ctx.fillText(String(lvl.r) + "  " + price.toFixed(2), xLabel, y - 6);
    }
    ctx.restore();
    if (hot) drawHandles([{ x: a.x, y: a.y }, { x: b.x, y: b.y }]);
  }

  // Fibonacci-transient: alleen het 0.5-niveau tussen de twee ankers, maar de
  // lijn loopt (anders dan de gewone fib-tool) door tot de rechterrand van
  // de pane, met een prijs-label in de lijnkleur — zo volg je een PCG-
  // transient over tijd zonder 'm steeds opnieuw te hoeven uittekenen.
  function paintFibTransient(ln, w, dash, hot) {
    const cs = getApi().candleSeries;
    const a = toPx(ln.a), b = toPx(ln.b);
    if (!a || !b) return;
    const pa = ln.a.y, pb = ln.b.y;
    const price = pa + (pb - pa) * FIB_TR_RATIO;
    const y = cs.priceToCoordinate(price);
    if (y == null) return;
    const x1 = Math.min(a.x, b.x);
    const xRight = paneW || container.clientWidth;

    ctx.save();
    ctx.strokeStyle = ln.color; ctx.lineWidth = w; ctx.setLineDash(dash);
    ctx.beginPath(); ctx.moveTo(x1, y); ctx.lineTo(xRight, y); ctx.stroke();
    ctx.setLineDash([]);

    const size = ln.size || 12;
    ctx.font = "700 " + size + "px 'JetBrains Mono', ui-monospace, monospace";
    const txt = price.toFixed(2);
    const tw = ctx.measureText(txt).width + 10;
    const th = size + 6;
    ctx.fillStyle = ln.color;
    ctx.fillRect(xRight - tw - 2, y - th / 2, tw, th);
    ctx.fillStyle = "#0b0e11";
    ctx.textBaseline = "middle"; ctx.textAlign = "left";
    ctx.fillText(txt, xRight - tw - 2 + 5, y + 1);
    ctx.restore();

    if (hot) drawHandles([{ x: a.x, y: a.y }, { x: b.x, y: b.y }]);
  }

  // Meetlat — zoals TradingView's "Date and price range"-tool: een gevulde
  // box tussen twee punten met een label dat het prijsverschil (+%),
  // tijdsduur (+aantal bars) en het volume ertussen toont. Kleur volgt de
  // richting (op/neer), niet de gekozen lijnkleur — vandaar dat de
  // kleur/dikte/stijl-opties in de editor voor dit type verborgen zijn.
  const MEASURE_FONT_PX = 9; // "tiny" — klein label, past onder de box zonder veel in de weg te zitten
  function formatDuration(sec) {
    sec = Math.max(0, Math.round(sec));
    const d = Math.floor(sec / 86400); sec -= d * 86400;
    const h = Math.floor(sec / 3600); sec -= h * 3600;
    const m = Math.floor(sec / 60);
    const parts = [];
    if (d) parts.push(d + "d");
    if (d || h) parts.push(h + "h");
    parts.push(m + "m");
    return parts.join(" ");
  }
  function formatVolume(v) {
    if (v >= 1e6) return (v / 1e6).toFixed(2) + "M";
    if (v >= 1e3) return (v / 1e3).toFixed(2) + "K";
    return v.toFixed(2);
  }
  function paintMeasure(ln, hot) {
    const a = toPx(ln.a), b = toPx(ln.b);
    if (!a || !b) return;
    const priceA = ln.a.y, priceB = ln.b.y;
    const diff = priceB - priceA;
    const pct = priceA ? (diff / priceA) * 100 : 0;
    const col = diff >= 0 ? C.up : C.down;
    const x = Math.min(a.x, b.x), y = Math.min(a.y, b.y);
    const rw = Math.abs(b.x - a.x), rh = Math.abs(b.y - a.y);

    ctx.save();
    ctx.globalAlpha = 0.14; ctx.fillStyle = col; ctx.fillRect(x, y, rw, rh); ctx.globalAlpha = 1;
    ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
    ctx.strokeRect(x, y, rw, rh); ctx.setLineDash([]);
    ctx.lineWidth = 1.5;
    ctx.beginPath(); ctx.moveTo(a.x, a.y); ctx.lineTo(b.x, b.y); ctx.stroke();

    // 50%-lijn: horizontaal op het midden van de prijsrange van de box.
    const yMid = (a.y + b.y) / 2;
    ctx.strokeStyle = col; ctx.lineWidth = 1; ctx.setLineDash([2, 2]);
    ctx.beginPath(); ctx.moveTo(x, yMid); ctx.lineTo(x + rw, yMid); ctx.stroke();
    ctx.setLineDash([]);

    // Bars + volume tussen de twee ankers (TIJD, dus timeframe-onafhankelijk).
    const cs = getCandles();
    const t0 = Math.min(ln.a.x, ln.b.x), t1 = Math.max(ln.a.x, ln.b.x);
    let bars = 0, vol = 0;
    if (cs && cs.length) {
      for (const c of cs) { if (c.time >= t0 && c.time <= t1) { bars++; vol += c.volume || 0; } }
    }
    const lines = [
      (diff >= 0 ? "+" : "") + diff.toFixed(2) + "  (" + (pct >= 0 ? "+" : "") + pct.toFixed(2) + "%)",
      formatDuration(t1 - t0) + (bars ? "  ·  " + bars + " bars" : ""),
      "Vol " + formatVolume(vol),
    ];

    ctx.font = "600 " + MEASURE_FONT_PX + "px 'JetBrains Mono', ui-monospace, monospace";
    const padX = 5, padY = 3, lineH = MEASURE_FONT_PX + 3;
    let tw = 0;
    for (const t of lines) tw = Math.max(tw, ctx.measureText(t).width);
    tw += padX * 2;
    const th = lineH * lines.length + padY * 2 - 2;
    const pw = paneW || container.clientWidth, ph = paneH || container.clientHeight;
    // Onder de box, horizontaal gecentreerd t.o.v. het vlak.
    const boxBottom = y + rh;
    let lx = x + rw / 2 - tw / 2, ly = boxBottom + 6;
    lx = Math.max(4, Math.min(lx, pw - tw - 4));
    if (ly + th > ph - 4) ly = y - th - 6; // past niet onder de box → dan erboven

    ctx.fillStyle = col;
    ctx.fillRect(lx, ly, tw, th);
    ctx.fillStyle = "#0b0e11";
    ctx.textBaseline = "top"; ctx.textAlign = "left";
    lines.forEach((t, i) => ctx.fillText(t, lx + padX, ly + padY + i * lineH));
    ctx.restore();

    if (hot) drawHandles([{ x: a.x, y: a.y }, { x: b.x, y: b.y }]);
  }

  function textSize(w) { return Math.round(10 + (w || 1.5) * 3); }
  function paintText(ln, hot) {
    const p = toPx(ln.a);
    if (!p || !ln.text) return;
    const size = ln.size || textSize(ln.width) || 16;
    ctx.save();
    ctx.font = size + "px 'JetBrains Mono', ui-monospace, monospace";
    ctx.textBaseline = "top"; ctx.textAlign = "left";
    ctx.lineWidth = 3; ctx.strokeStyle = "rgba(11,14,17,0.85)";
    ctx.strokeText(ln.text, p.x, p.y);
    ctx.fillStyle = ln.color; ctx.fillText(ln.text, p.x, p.y);
    if (hot) {
      const wpx = ctx.measureText(ln.text).width;
      ctx.strokeStyle = "#ffd24a"; ctx.lineWidth = 1; ctx.setLineDash([3, 3]);
      ctx.strokeRect(p.x - 3, p.y - 3, wpx + 6, size + 6); ctx.setLineDash([]);
    }
    ctx.restore();
  }

  function renderAll() {
    const w = paneW, h = paneH; // eslint-disable-line no-unused-vars

    let selSeg = null, selLine = null;
    if (linesVisible) {
      for (let i = 0; i < lines.length; i++) {
        const ln = lines[i];
        const s = segPixels(ln);
        if (!s) continue;
        const isSel = i === selected, isHov = i === hover;
        paintShape(ln, isSel, isHov);
        if (isSel) { selSeg = s; selLine = ln; }
      }
    }

    if (draft) paintShape(draft, false, true);

    if (magnet && snapPreview && (tool !== "cursor" || drag)) {
      const p = toPx(snapPreview);
      if (p) {
        ctx.strokeStyle = LINE_COL; ctx.lineWidth = 1.5; ctx.setLineDash([]);
        ctx.beginPath(); ctx.arc(p.x, p.y, 5, 0, Math.PI * 2); ctx.stroke();
        ctx.beginPath();
        ctx.moveTo(p.x - 8, p.y); ctx.lineTo(p.x + 8, p.y);
        ctx.moveTo(p.x, p.y - 8); ctx.lineTo(p.x, p.y + 8);
        ctx.stroke();
      }
    }

    if (selSeg && selLine) editorPosition(selSeg, selLine);
    else editorHide();
  }
  function scheduleRedraw() { if (requestUpdate) requestUpdate(); }

  // ── Interactie ───────────────────────────────────────────────
  function localXY(ev) {
    const r = container.getBoundingClientRect();
    return { x: ev.clientX - r.left, y: ev.clientY - r.top };
  }

  function onDown(ev) {
    if (ev.button !== 0) return;
    const { x, y } = localXY(ev);

    if (tool === "brush") {
      draft = { id: newId(), type: "brush", points: [rawAnchor(x, y)], ...curStyle };
      brushLastPx = { x, y };
      snapPreview = null;
      scheduleRedraw();
      return;
    }
    if (tool !== "cursor") return;

    const hit = handleHit(x, y);
    if (!hit) return;
    selected = hit.idx;
    const L = lines[hit.idx];
    drag = {
      part: hit.part, idx: hit.idx, moved: false,
      startRaw: rawAnchor(x, y),
      origA: L.a ? { ...L.a } : null, origB: L.b ? { ...L.b } : null,
      origPoints: L.points ? L.points.map((p) => ({ x: p.x, y: p.y })) : null,
    };
    setChartLocked(true);
    container.style.cursor = "grabbing";
    scheduleRedraw();
  }

  function onMove(ev) {
    const { x, y } = localXY(ev);
    curPx = x; curPy = y; // eslint-disable-line no-unused-vars

    if (draft && draft.type === "brush") {
      if ((ev.buttons & 1) && draft.points.length < BRUSH_MAX_PTS) {
        if (Math.hypot(x - brushLastPx.x, y - brushLastPx.y) >= BRUSH_MIN_PX) {
          draft.points.push(rawAnchor(x, y));
          brushLastPx = { x, y };
        }
      }
      scheduleRedraw();
      return;
    }

    if (drag) {
      drag.moved = true;
      const ln = lines[drag.idx];

      if (drag.part === "line") {
        const cur = rawAnchor(x, y);
        let dx = cur.x - drag.startRaw.x, dy = cur.y - drag.startRaw.y;
        if (ev.shiftKey) {
          const s0 = toPx(drag.startRaw);
          if (s0) { if (Math.abs(x - s0.x) >= Math.abs(y - s0.y)) dy = 0; else dx = 0; }
        }
        if (ln.type === "brush" && drag.origPoints) {
          ln.points = drag.origPoints.map((p) => ({ x: p.x + dx, y: p.y + dy }));
        } else {
          if (drag.origA) ln.a = { x: drag.origA.x + dx, y: drag.origA.y + dy };
          if (drag.origB) ln.b = { x: drag.origB.x + dx, y: drag.origB.y + dy };
        }
        snapPreview = null;
      } else if (drag.part === "hline") {
        const anc = magnet ? magnetAnchor(x, y) : rawAnchor(x, y);
        ln.a = { x: ln.a.x, y: anc.y }; ln.b = ln.a;
        snapPreview = magnet ? anc : null;
      } else {
        const moveKey = drag.part === "p1" ? "a" : "b";
        const otherKey = drag.part === "p1" ? "b" : "a";
        const fixedPx = toPx(ln[otherKey]);
        const anc = anchorFromPixel(x, y, ev, fixedPx);
        ln[moveKey] = anc;
        snapPreview = (magnet && !ev.shiftKey) ? anc : null;
      }
      const now = performance.now();
      if (now - lastUpdateEmit > 40) { lastUpdateEmit = now; emit("update", { line: cloneLine(lines[drag.idx]) }); }
      scheduleRedraw();
      return;
    }

    if (draft) {
      const fixedPx = toPx(draft.a);
      draft.b = anchorFromPixel(x, y, ev, fixedPx);
      snapPreview = (magnet && !ev.shiftKey) ? magnetAnchor(x, y) : null;
      scheduleRedraw();
      return;
    }

    if (tool === "cursor") {
      snapPreview = null;
      const hit = handleHit(x, y);
      const h = hit ? hit.idx : -1;
      const cur = !hit ? "" : (hit.part === "p1" || hit.part === "p2") ? "grab" : "move";
      if (h !== hover || container.style.cursor !== cur) {
        hover = h;
        container.style.cursor = cur;
        scheduleRedraw();
      }
      return;
    }

    snapPreview = (magnet && tool !== "brush") ? magnetAnchor(x, y) : null;
    scheduleRedraw();
  }

  function onUp() {
    if (draft && draft.type === "brush") {
      if (draft.points.length >= 2) {
        lines.push(draft);
        selected = lines.length - 1;
        emit("add", { line: cloneLine(draft) });
        suppressClick = true;
      }
      draft = null;
      scheduleRedraw();
      return;
    }
    if (!drag) return;
    const moved = drag.moved;
    const movedLine = lines[drag.idx];
    drag = null;
    suppressClick = moved;
    setChartLocked(false);
    container.style.cursor = hover >= 0 ? "move" : "";
    if (moved && movedLine) emit("update", { line: cloneLine(movedLine) });
    scheduleRedraw();
  }

  function onClick(ev) {
    if (suppressClick) { suppressClick = false; return; }
    if (tool === "brush") return;
    const { x, y } = localXY(ev);

    if (tool === "cursor") {
      const hit = handleHit(x, y);
      selected = hit ? hit.idx : -1;
      scheduleRedraw();
      return;
    }

    if (tool === "hline") {
      const a = anchorFromPixel(x, y);
      const line = { id: newId(), type: "hline", a, b: a, ...curStyle };
      lines.push(line);
      selected = lines.length - 1;
      emit("add", { line: cloneLine(line) });
      scheduleRedraw();
      return;
    }

    if (tool === "text") {
      const str = window.prompt("Tekst:");
      if (str && str.trim()) {
        const line = { id: newId(), type: "text", a: rawAnchor(x, y), text: str.trim(), ...curStyle };
        lines.push(line);
        selected = lines.length - 1;
        emit("add", { line: cloneLine(line) });
      }
      scheduleRedraw();
      return;
    }

    // trend / ray / rechthoek / fibonacci → twee kliks
    if (!draft) {
      draft = { id: newId(), type: tool, a: anchorFromPixel(x, y), b: anchorFromPixel(x, y), ...curStyle };
    } else {
      draft.b = anchorFromPixel(x, y, ev, toPx(draft.a));
      lines.push(draft);
      selected = lines.length - 1;
      emit("add", { line: cloneLine(draft) });
      draft = null;
      snapPreview = null;
    }
    scheduleRedraw();
  }

  function onContext(ev) {
    if (tool !== "cursor" || draft) {
      ev.preventDefault();
      draft = null;
      scheduleRedraw();
    }
  }

  const PASTE_OFFSET_PX = 24; // kopie verschijnt 24px rechtsonder het origineel

  function copySelected() {
    if (selected < 0) return;
    clipboard = cloneLine(lines[selected]);
  }

  // Bouwt een nieuwe lijn op basis van het klembord, verschoven zodat de
  // kopie niet precies op het origineel landt. Werkt voor elk type (lijn,
  // rechthoek, fib(tr), brush-streek, tekst) omdat alleen de aanwezige
  // ankers (a/b of points) verschoven worden — de rest (kleur/dikte/stijl/
  // tekst/grootte) komt letterlijk mee.
  function pasteClipboard() {
    if (!clipboard) return;
    const src = clipboard;
    const copy = { id: newId(), type: src.type, color: src.color, width: src.width, style: src.style };
    if (src.text !== undefined) copy.text = src.text;
    if (src.size !== undefined) copy.size = src.size;

    if (src.points) {
      copy.points = src.points.map((p) => shiftPoint(p, PASTE_OFFSET_PX, PASTE_OFFSET_PX));
    } else if (src.a) {
      const newA = shiftPoint(src.a, PASTE_OFFSET_PX, PASTE_OFFSET_PX);
      copy.a = newA;
      if (src.b) copy.b = src.type === "hline" ? newA : shiftPoint(src.b, PASTE_OFFSET_PX, PASTE_OFFSET_PX);
    }

    lines.push(copy);
    selected = lines.length - 1;
    hover = -1;
    emit("add", { line: cloneLine(copy) });
    scheduleRedraw();
  }

  function duplicateSelected() {
    copySelected();
    pasteClipboard();
  }

  function onKey(ev) {
    const t = ev.target;
    const inField = t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA");

    if (ev.key === "Escape") {
      if (draft) { draft = null; scheduleRedraw(); }
      else if (tool !== "cursor") { setTool("cursor"); }
      else if (selected >= 0) { selected = -1; scheduleRedraw(); }
    } else if ((ev.key === "Delete" || ev.key === "Backspace") && selected >= 0) {
      if (inField) return;
      const delId = lines[selected].id;
      lines.splice(selected, 1); selected = -1; hover = -1;
      emit("delete", { id: delId });
      scheduleRedraw();
    } else if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === "c" && selected >= 0) {
      if (inField) return;
      copySelected();
    } else if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === "v" && clipboard) {
      if (inField) return;
      ev.preventDefault();
      pasteClipboard();
    }
  }

  container.addEventListener("mousedown", onDown);
  container.addEventListener("mousemove", onMove);
  window.addEventListener("mouseup", onUp);
  container.addEventListener("click", onClick);
  container.addEventListener("contextmenu", onContext);
  document.addEventListener("keydown", onKey);

  // ── Lijn-editor (kleur / dikte / stijl / verwijderen) ────────
  const editor = document.createElement("div");
  editor.className = "dt-editor";
  ["click", "mousedown", "mousemove", "contextmenu", "dblclick"].forEach((ev) =>
    editor.addEventListener(ev, (e) => e.stopPropagation()));
  container.appendChild(editor);

  const swatchEls = [], widthEls = [], styleEls = [], sizeEls = [];

  function sep() { const d = document.createElement("div"); d.className = "sep"; return d; }
  function svgLine(w, style) {
    const dash = style === "dashed" ? "5 3" : style === "dotted" ? "1.5 2.5" : "";
    return `<svg width="20" height="12"><line x1="1" y1="6" x2="19" y2="6" stroke="#e6e8ea" ` +
      `stroke-width="${Math.min(w, 3)}" ${dash ? `stroke-dasharray="${dash}"` : ""} stroke-linecap="round"/></svg>`;
  }
  function applyStyle(patch) {
    Object.assign(curStyle, patch);
    if (selected >= 0) { Object.assign(lines[selected], patch); emit("update", { line: cloneLine(lines[selected]) }); }
    editorSync();
    scheduleRedraw();
  }
  function editorSync() {
    const ln = selected >= 0 ? lines[selected] : curStyle;
    swatchEls.forEach((s, i) => s.classList.toggle("on", PALETTE[i] === ln.color));
    widthEls.forEach((b, i) => b.classList.toggle("on", WIDTHS[i] === ln.width));
    styleEls.forEach((b, i) => b.classList.toggle("on", STYLES[i] === ln.style));
    sizeEls.forEach((b, i) => b.classList.toggle("on", TEXT_SIZES[i] === (ln.size || 12)));
  }
  function editorHide() { editor.style.display = "none"; }
  function editorPosition(seg, line) {
    editor.style.display = "flex";
    editorConfigureFor(line.type);
    editorSync();
    const w = container.clientWidth, h = container.clientHeight;
    const ew = editor.offsetWidth || 220, eh = editor.offsetHeight || 34;
    let mx, my;
    if (line.type === "hline") { mx = Math.min(Math.max(curPx, 20), w - 20); my = seg.ay; }
    else { mx = (seg.ax + seg.bx) / 2; my = (seg.ay + seg.by) / 2; }
    let left = Math.max(6, Math.min(mx - ew / 2, w - ew - 6));
    let top = my - eh - 14;
    if (top < 6) top = my + 14;
    top = Math.max(6, Math.min(top, h - eh - 6));
    editor.style.left = left + "px";
    editor.style.top = top + "px";
  }

  const gCol = document.createElement("div"); gCol.className = "grp";
  PALETTE.forEach((col) => {
    const s = document.createElement("div"); s.className = "sw"; s.style.background = col; s.title = col;
    s.addEventListener("click", () => applyStyle({ color: col }));
    swatchEls.push(s); gCol.appendChild(s);
  });
  editor.appendChild(gCol); const sepCol = sep(); editor.appendChild(sepCol);

  const gW = document.createElement("div"); gW.className = "grp";
  WIDTHS.forEach((wv, i) => {
    const b = document.createElement("div"); b.className = "opt"; b.title = "Dikte " + (i + 1);
    b.innerHTML = svgLine(wv, "solid");
    b.addEventListener("click", () => applyStyle({ width: wv }));
    widthEls.push(b); gW.appendChild(b);
  });
  editor.appendChild(gW); const sepW = sep(); editor.appendChild(sepW);

  const gS = document.createElement("div"); gS.className = "grp";
  STYLES.forEach((stl) => {
    const b = document.createElement("div"); b.className = "opt"; b.title = stl;
    b.innerHTML = svgLine(2, stl);
    b.addEventListener("click", () => applyStyle({ style: stl }));
    styleEls.push(b); gS.appendChild(b);
  });
  editor.appendChild(gS); const sepS = sep(); editor.appendChild(sepS);

  const gSize = document.createElement("div"); gSize.className = "grp";
  TEXT_SIZES.forEach((sz, i) => {
    const b = document.createElement("div"); b.className = "opt"; b.title = "Grootte " + sz;
    b.innerHTML = `<span style="font-weight:700; line-height:1; font-size:${10 + i * 2}px">A</span>`;
    b.addEventListener("click", () => applyStyle({ size: sz }));
    sizeEls.push(b); gSize.appendChild(b);
  });
  editor.appendChild(gSize); const sepSize = sep(); editor.appendChild(sepSize);

  function editorConfigureFor(type) {
    const isText = type === "text";
    const isMeasure = type === "measure"; // kleur/dikte/stijl zijn hier niet instelbaar: de box-kleur volgt automatisch de richting (op/neer)
    const hideStyle = isText || isMeasure;
    const showSize = isText || type === "fibtr"; // fib-transient heeft ook een instelbaar prijs-label
    gCol.style.display = isMeasure ? "none" : "";
    sepCol.style.display = isMeasure ? "none" : "";
    gW.style.display = hideStyle ? "none" : "";
    sepW.style.display = hideStyle ? "none" : "";
    gS.style.display = hideStyle ? "none" : "";
    sepS.style.display = hideStyle ? "none" : "";
    gSize.style.display = showSize ? "" : "none";
    sepSize.style.display = showSize ? "" : "none";
  }

  editor.appendChild(sep());
  const dupBtn = document.createElement("div"); dupBtn.className = "dup"; dupBtn.title = "Dupliceren (Ctrl+C / Ctrl+V)"; dupBtn.textContent = "⧉";
  dupBtn.addEventListener("click", () => { if (selected >= 0) duplicateSelected(); });
  editor.appendChild(dupBtn);

  const delBtn = document.createElement("div"); delBtn.className = "del"; delBtn.title = "Verwijderen"; delBtn.textContent = "🗑";
  delBtn.addEventListener("click", () => {
    if (selected >= 0) { const delId = lines[selected].id; lines.splice(selected, 1); selected = -1; hover = -1; emit("delete", { id: delId }); editorHide(); scheduleRedraw(); }
  });
  editor.appendChild(delBtn);

  editorHide();

  // ── Plugin-primitive: de library tekent onze lagen mee ───────
  const paneRenderer = {
    draw(target) {
      target.useMediaCoordinateSpace((scope) => {
        ctx = scope.context;
        paneW = scope.mediaSize.width;
        paneH = scope.mediaSize.height;
        renderAll();
      });
    },
  };
  const paneView = {
    zOrder: () => "top",
    renderer: () => paneRenderer,
  };
  const primitive = {
    attached: (p) => { requestUpdate = p.requestUpdate; },
    detached: () => { requestUpdate = null; },
    updateAllViews: () => {},
    paneViews: () => [paneView],
  };
  try { getApi().candleSeries.attachPrimitive(primitive); }
  catch (e) { console.warn("[draw] attachPrimitive faalde:", e); }

  // ── Publieke API ─────────────────────────────────────────────
  function setChartLocked(lock) {
    getApi().chart.applyOptions({ handleScroll: !lock, handleScale: !lock });
  }

  function setTool(name) {
    tool = name;
    draft = null;
    drag = null;
    suppressClick = false;
    snapPreview = null;
    if (name !== "cursor") { selected = -1; hover = -1; }
    setChartLocked(name !== "cursor");
    container.style.cursor = name === "cursor" ? "" : "crosshair";
    scheduleRedraw();
    if (toolChangeCb) toolChangeCb(tool, magnet);
  }

  function destroy() {
    container.removeEventListener("mousedown", onDown);
    container.removeEventListener("mousemove", onMove);
    window.removeEventListener("mouseup", onUp);
    container.removeEventListener("click", onClick);
    container.removeEventListener("contextmenu", onContext);
    document.removeEventListener("keydown", onKey);
    try { getApi().candleSeries.detachPrimitive(primitive); } catch {}
    try { editor.remove(); } catch {}
  }

  return {
    redraw: scheduleRedraw,
    setTool,
    getTool: () => tool,
    toggleMagnet: () => { magnet = !magnet; if (!magnet) snapPreview = null; scheduleRedraw(); if (toolChangeCb) toolChangeCb(tool, magnet); return magnet; },
    isMagnet: () => magnet,
    clearAll: () => { lines.length = 0; draft = null; selected = -1; hover = -1; emit("clear", {}); scheduleRedraw(); },
    count: () => lines.length,
    // Toon/verberg alle tekeningen — lokaal, niet gebroadcast (zie ook de
    // toelichting bij `linesVisible` hierboven).
    toggleVisibility: () => {
      linesVisible = !linesVisible;
      if (!linesVisible) { selected = -1; hover = -1; }
      scheduleRedraw();
      return linesVisible;
    },
    areLinesVisible: () => linesVisible,
    onToolChange: (cb) => { toolChangeCb = cb; },
    destroy,

    // ── Live-integratie (coach/student) — nog niet aangesloten ──
    onLocalEdit: (cb) => { localEditCb = cb; },
    getAllLines: () => lines.map(cloneLine),
    applyRemote: (ev) => {
      applyingRemote = true;
      try {
        const findIdx = (id) => lines.findIndex((l) => l.id === id);
        if (ev.type === "add") {
          if (findIdx(ev.line.id) < 0) lines.push(cloneLine(ev.line));
        } else if (ev.type === "update") {
          const i = findIdx(ev.line.id);
          if (i >= 0) lines[i] = cloneLine(ev.line); else lines.push(cloneLine(ev.line));
        } else if (ev.type === "delete") {
          const i = findIdx(ev.id);
          if (i >= 0) { lines.splice(i, 1); if (selected === i) selected = -1; }
        } else if (ev.type === "clear") {
          lines.length = 0; selected = -1; hover = -1;
        } else if (ev.type === "replaceAll") {
          lines.length = 0; (ev.lines || []).forEach((l) => lines.push(cloneLine(l))); selected = -1;
        }
      } finally { applyingRemote = false; }
      scheduleRedraw();
    },
  };
}
